import { HttpStatusCode } from "axios";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import type { ProposalFirmSendResultJSON } from "work/proposals/bidding/_api/proposal-firm-send-api";

/**
 * The last time a draft was sent to several firms, as its page remembers it (#576).
 *
 * The server finds a firm's copy by the send's command id and sends that copy as it was drafted. So
 * the id is resent only with the same terms: after an edit, an old copy would carry the old terms.
 */
export type ProposalFirmSendAttempt = {
  /** Resent on every retry of these terms, so no firm is drafted twice. */
  commandId: string;
  /** The terms the attempt sent (`proposalFirmSendTerms`). */
  terms: string;
  /** Copies the attempt drafted that no firm received. Undefined until the server has answered. */
  unsentCopyIds?: string[];
};

/** How to send a draft's terms, given the last attempt. */
export type ProposalFirmSendPlan = {
  attempt: ProposalFirmSendAttempt;
  /** Copies an attempt with other terms drafted and never sent. They are deleted, never sent. */
  staleCopyIds: string[];
  /** An attempt with other terms got no answer, so it may have drafted or sent copies nobody can name. */
  earlierOutcomeUnknown: boolean;
};

/**
 * The same terms are a retry, and keep the command id. Other terms are a new send, with a new id,
 * and the copies the last attempt left unsent are stale.
 */
export function planProposalFirmSend(
  previous: ProposalFirmSendAttempt | undefined,
  terms: string,
  mintCommandId: () => string,
): ProposalFirmSendPlan {
  if (previous?.terms === terms) return { attempt: previous, staleCopyIds: [], earlierOutcomeUnknown: false };
  return {
    attempt: { commandId: mintCommandId(), terms },
    staleCopyIds: previous?.unsentCopyIds ?? [],
    earlierOutcomeUnknown: previous !== undefined && previous.unsentCopyIds === undefined,
  };
}

/** The attempt once the server answered with a result per firm. */
export function recordProposalFirmSendResults(
  attempt: ProposalFirmSendAttempt,
  results: readonly ProposalFirmSendResultJSON[],
): ProposalFirmSendAttempt {
  const unsentCopyIds = results.flatMap((result) =>
    result.outcome === "NotSent" && result.proposalId ? [result.proposalId] : [],
  );
  return { ...attempt, unsentCopyIds };
}

/**
 * The attempt once the send failed. The server refuses a whole send (4xx) before it drafts any firm,
 * so that attempt left no copies. Any other failure leaves the outcome unknown.
 */
export function recordProposalFirmSendFailure(attempt: ProposalFirmSendAttempt, error: unknown): ProposalFirmSendAttempt {
  const status = getApiErrorStatus(error);
  const refused =
    typeof status === "number" && status >= HttpStatusCode.BadRequest && status < HttpStatusCode.InternalServerError;
  return refused ? { ...attempt, unsentCopyIds: [] } : attempt;
}
